
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, RefreshCw, Wallet, AlertTriangle, CheckCircle, XCircle, MoreHorizontal, Plus, Minus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import CreditAdjustmentDialog from "./credits/CreditAdjustmentDialog";

interface UserCreditAvailability {
  id: string;
  username: string | null;
  full_name: string | null;
  email: string;
  credits_balance: number;
  credits_used: number;
  available_credits: number;
  subscription_type: string;
  subscription_status: string;
}

interface CreditAvailabilityPanelProps {
  refreshTrigger?: number;
}

export default function CreditAvailabilityPanel({ refreshTrigger }: CreditAvailabilityPanelProps) {
  const [users, setUsers] = useState<UserCreditAvailability[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserCreditAvailability[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("available_desc");
  
  // Dialog state
  const [selectedUser, setSelectedUser] = useState<UserCreditAvailability | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchCreditAvailability();
  }, [refreshTrigger]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [users, searchTerm, filterType, sortBy]);

  const fetchCreditAvailability = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.rpc('get_admin_users_data');
      
      if (error) {
        console.error('Database error:', error);
        toast.error(`Failed to load credit availability: ${error.message}`);
        return;
      }
      
      // Transform data to include available credits calculation
      const transformedData = (data || []).map(user => ({
        ...user,
        available_credits: Math.max(0, user.credits_balance - user.credits_used)
      }));
      
      setUsers(transformedData);
      console.log('Successfully loaded credit availability for', transformedData.length, 'users');
    } catch (error: any) {
      console.error('Error fetching credit availability:', error);
      toast.error(`Failed to load credit availability: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(searchLower) ||
        user.username?.toLowerCase().includes(searchLower) ||
        user.full_name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply credit availability filter
    switch (filterType) {
      case "zero":
        filtered = filtered.filter(user => user.available_credits === 0);
        break;
      case "low":
        filtered = filtered.filter(user => user.available_credits > 0 && user.available_credits < 5);
        break;
      case "healthy":
        filtered = filtered.filter(user => user.available_credits >= 5);
        break;
      case "free":
        filtered = filtered.filter(user => user.subscription_type === "free");
        break;
      case "paid":
        filtered = filtered.filter(user => user.subscription_type !== "free");
        break;
      // "all" shows everything
    }

    // Apply sorting
    switch (sortBy) {
      case "available_asc":
        filtered.sort((a, b) => a.available_credits - b.available_credits);
        break;
      case "available_desc":
        filtered.sort((a, b) => b.available_credits - a.available_credits);
        break;
      case "name":
        filtered.sort((a, b) => {
          const nameA = a.full_name || a.username || a.email;
          const nameB = b.full_name || b.username || b.email;
          return nameA.localeCompare(nameB);
        });
        break;
      case "subscription":
        filtered.sort((a, b) => a.subscription_type.localeCompare(b.subscription_type));
        break;
    }

    setFilteredUsers(filtered);
  };

  const getCreditStatusIcon = (availableCredits: number) => {
    if (availableCredits === 0) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    } else if (availableCredits < 5) {
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    } else {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  const getCreditStatusBadge = (availableCredits: number) => {
    if (availableCredits === 0) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          {availableCredits}
        </Badge>
      );
    } else if (availableCredits < 5) {
      return (
        <Badge variant="secondary" className="flex items-center gap-1 bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3" />
          {availableCredits}
        </Badge>
      );
    } else {
      return (
        <Badge variant="default" className="flex items-center gap-1 bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3" />
          {availableCredits}
        </Badge>
      );
    }
  };

  const getSubscriptionBadgeVariant = (type: string) => {
    switch (type) {
      case 'yearly': return 'default';
      case 'monthly': return 'secondary';
      default: return 'outline';
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setSortBy("available_desc");
  };

  const handleUserAction = (user: UserCreditAvailability) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    fetchCreditAvailability();
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-4">
        {/* Title and Actions Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6" />
            <h1 className="text-2xl font-semibold">Credit Availability</h1>
            <Badge variant="outline" className="text-sm">
              {filteredUsers.length} users
            </Badge>
          </div>
          <Button variant="outline" size="sm" onClick={fetchCreditAvailability} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters and Search Row */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="zero">🔴 Zero Credits</SelectItem>
                  <SelectItem value="low">🟡 Low Credits (&lt;5)</SelectItem>
                  <SelectItem value="healthy">🟢 Healthy Credits (≥5)</SelectItem>
                  <SelectItem value="free">Free Plan Users</SelectItem>
                  <SelectItem value="paid">Paid Plan Users</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available_desc">Available Credits (High to Low)</SelectItem>
                  <SelectItem value="available_asc">Available Credits (Low to High)</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="subscription">Subscription Type</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm" onClick={resetFilters}>
                Reset
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Total Users:</span>
            <span className="font-medium text-foreground">{filteredUsers.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <span>Zero Credits:</span>
            <span className="font-medium text-red-600">
              {filteredUsers.filter(u => u.available_credits === 0).length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <span>Low Credits:</span>
            <span className="font-medium text-yellow-600">
              {filteredUsers.filter(u => u.available_credits > 0 && u.available_credits < 5).length}
            </span>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 rounded-full border-2 border-t-transparent border-primary animate-spin"></div>
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Used</TableHead>
                <TableHead>Available</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                    {searchTerm || filterType !== "all" ? 'No users match your criteria' : 'No users found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {user.full_name || user.username || 'Unnamed User'}
                        </div>
                        {user.username && user.full_name && (
                          <div className="text-sm text-muted-foreground">@{user.username}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={getSubscriptionBadgeVariant(user.subscription_type)}>
                        {user.subscription_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{user.credits_balance}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{user.credits_used}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-bold text-lg">
                        {getCreditStatusBadge(user.available_credits)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCreditStatusIcon(user.available_credits)}
                        <span className="text-sm">
                          {user.subscription_status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleUserAction(user)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Credits
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleUserAction(user)}>
                            <Minus className="h-4 w-4 mr-2" />
                            Subtract Credits
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleUserAction(user)}>
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reset Usage
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Credit Adjustment Dialog */}
      <CreditAdjustmentDialog
        user={selectedUser}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleDialogSuccess}
      />
    </div>
  );
}
